'use client'

import { motion } from 'motion/react'
import Link from 'next/link'
import { useLocale } from 'next-intl'
import { ReactNode } from 'react'
import { Locale } from '@/setup/locales'
import { getLinkHref, ILinkParams } from '@/setup/routing'

export interface INavigationDesktopItemProps {
    linkParams: ILinkParams | { externalUrl: string; target?: '_blank' | '_self' }
    label: ReactNode
    isActive: boolean
    isHovered?: boolean
    onMouseEnter?: () => void
    onMouseLeave?: () => void
    gtmId?: string
}

export const NavigationDesktopItem = ({
    linkParams,
    label,
    onMouseEnter,
    onMouseLeave,
    isHovered,
    gtmId,
}: INavigationDesktopItemProps) => {
    const locale = useLocale()

    const innerNode = (
        <div
            className="px-[18px] py-[8px] font-medium relative h-[40px]"
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            {isHovered && (
                <motion.div
                    key="navigation-highlighter"
                    layoutId="navigation-highlighter"
                    className="bg-black/10 absolute w-full h-full top-0 left-0 rounded-l"
                    transition={{
                        layout: {
                            duration: 0.15,
                            type: 'spring',
                            bounce: 0.05,
                        },
                    }}
                />
            )}

            <motion.div className="relative w-full h-full select-none overflow-hidden">
                <span className="font-medium w-full">{label}</span>
            </motion.div>
        </div>
    )

    if ('externalUrl' in linkParams) {
        return (
            <a
                className={gtmId}
                href={linkParams.externalUrl}
                target={linkParams.target}
                rel="noreferrer"
            >
                {innerNode}
            </a>
        )
    }

    return (
        <Link className={gtmId} href={getLinkHref(linkParams, locale as Locale)}>
            {innerNode}
        </Link>
    )
}
